// -----------------------------------------------------------------------
// <copyright file="appContext.ts" company="Microsoft">
//     copyright (c) Microsoft. All rights reserved.
// </copyright>
// -----------------------------------------------------------------------

import { v4 as uuidv4 } from 'uuid';

import { DefaultCultureMapping, FirstVersionToShowAppBar, SMCTicketing } from '../../models/constants/getHelpConstants';
import { EmeraldUserType } from './emeraldUserType';
import {
  EnvironmentConfig,
  GetHelpBaseUrl,
  devEnvironment,
  ppeEnvironment,
  prodEnvironment,
} from './environmentConfig';
import { SystemInfo } from './experienceConfigurations';

/**
 * App context model definition
 */
export type AppContext = {
  appId: string;
  locale: string;
  sessionId: string;
  correlationVector: string;
  deeplinkPath: string;
  theme: string;
  accentColor: string;
  diagcap: string;
  emeraldUserType: EmeraldUserType;
  isCommercial: boolean;
  featureFlightList: string[];
  appver: string;
  appSupportsConfigUpdate: boolean;
  environment: EnvironmentConfig;
  appSupportsAuth: boolean;
  distUrl: string;
  systemInfo: SystemInfo;
  deviceId: string;
  manufacturer: string;
  manufacturerLink: string;
  degradedExperience: boolean;
  startupCheckDafSessionId: string;
  showAboutAndFeedbackButtons: boolean;
};

let initialAppContext: AppContext = null;

const mapToDefaultCultureIfMissing = (locale: string): string => {
  return DefaultCultureMapping.get(locale.toLowerCase()) ?? locale;
};

const getEmeraldUserType = (): EmeraldUserType => {
  const path = window.location.pathname.substring(1).toLocaleLowerCase();
  let experience: EmeraldUserType = EmeraldUserType.Consumer;
  switch (path) {
    case 'gethelp':
    case 'emerald':
      experience = EmeraldUserType.Consumer;
      break;
    case 'gethelpcommercial':
    case 'emeraldcommercial':
      experience = EmeraldUserType.Commercial;
      break;
    default:
      experience = EmeraldUserType.Consumer;
      break;
  }
  return experience;
};

const getEnvironment = (): EnvironmentConfig => {
  const path = window.location.origin.toLocaleLowerCase();
  let env: EnvironmentConfig;
  switch (path) {
    case GetHelpBaseUrl.Prod:
      env = prodEnvironment;
      break;
    case GetHelpBaseUrl.PPE:
      env = ppeEnvironment;
      break;
    case GetHelpBaseUrl.Dev:
    case GetHelpBaseUrl.Local:
      env = devEnvironment;
      break;
    default:
      env = prodEnvironment;
      break;
  }
  return env;
};

const skylightContext = window && window['skylightContext'];

const initializeAppContext = (): AppContext => {
  const urlParams = new URLSearchParams(window.location.search);
  const userType = getEmeraldUserType();
  const environment = getEnvironment();
  const appVer = urlParams.get('appver');
  const diagcap = urlParams.get('diagcap');
  const degradedExperience = urlParams.get('degradedExperience')?.toLowerCase() === 'true';
  let appSupportsConfigUpdate = false;
  let appSupportsAuth = false;
  let showAboutAndFeedbackButtons = false;
  if (appVer && !isNaN(Number(appVer))) {
    appSupportsConfigUpdate = Number(appVer) >= 2010;
    appSupportsAuth = Number(appVer) >= SMCTicketing.AuthSupportAppVersion;
    showAboutAndFeedbackButtons = Number(appVer) >= FirstVersionToShowAppBar;
  }

  initialAppContext = {
    appId: 'Osefo',
    locale: mapToDefaultCultureIfMissing(urlParams.get('locale') ?? skylightContext?.locale ?? 'en-US'),
    sessionId: uuidv4(),
    correlationVector: urlParams.get('cv') ?? '0000000000000000000000.0',
    deeplinkPath: urlParams.get('deeplink') ?? '',
    theme: urlParams.get('theme') ?? '',
    accentColor: urlParams.get('color') ?? '',
    diagcap: !!diagcap ? diagcap.replace(/msdt/g, 'dafv1').replace(/rt/g, 'dafv2') : '',
    emeraldUserType: userType,
    appver: appVer ?? '',
    environment: environment,
    isCommercial: userType === EmeraldUserType.Commercial,
    distUrl: window?.emeraldContext?.cdnPath,
    appSupportsConfigUpdate: appSupportsConfigUpdate,
    appSupportsAuth: appSupportsAuth,
    deviceId: null,
    systemInfo: null,
    featureFlightList: [],
    manufacturer: '',
    manufacturerLink: '',
    degradedExperience: degradedExperience,
    startupCheckDafSessionId: null,
    showAboutAndFeedbackButtons: showAboutAndFeedbackButtons,
  };
  return initialAppContext;
};

export const appContext = initialAppContext ?? initializeAppContext();
